import { useEffect, useRef, useState } from "react";
import { Download, Upload, Copy, Check, RefreshCw, Server, Package, Cloud } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { API_BASE, BACKEND_URL, api, formatApiError } from "@/lib/api";
import { INSTALL_SH, UPGRADE_SH, GITHUB_PUSH_MD } from "@/lib/tech-scripts";

const PHASE_LABELS = {
  idle: "In attesa",
  preparing: "Preparazione...",
  scheduled_restart: "Riavvio in coda...",
  restarting: "Riavvio servizi...",
  verifying: "Verifica salute backend...",
  rolling_back: "Rollback in corso...",
  done_ok: "Aggiornamento completato",
  done_failed: "Aggiornamento fallito — rollback eseguito",
};

const PHASE_TONE = {
  done_ok: "text-green-700 bg-green-50 border-green-300",
  done_failed: "text-red-700 bg-red-50 border-red-300",
  rolling_back: "text-amber-700 bg-amber-50 border-amber-300",
};

function ScriptBox({ title, filename, body, icon: Icon }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success("Copiato negli appunti");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copia fallita");
    }
  };
  const download = () => {
    const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="border border-[color:var(--afb-cream)] rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-[color:var(--afb-cream)]/50">
        <div className="flex items-center gap-2 text-sm font-medium text-afb-ink">
          {Icon ? <Icon className="w-4 h-4" /> : null}
          <span>{title}</span>
          <code className="text-xs text-afb-ink/60">{filename}</code>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={copy}
            data-testid={`tech-copy-${filename}`}
          >
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            Copia
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={download}
            data-testid={`tech-download-${filename}`}
          >
            <Download className="w-4 h-4 mr-1" />
            Scarica
          </Button>
        </div>
      </div>
      <pre className="p-4 bg-[#0b1320] text-[#d6e4ff] text-xs overflow-auto max-h-72">
{body}
      </pre>
    </div>
  );
}

export default function TechTab() {
  const { authHeaders } = useAuth();
  const [busy, setBusy] = useState(false);
  const [withModules, setWithModules] = useState(true);
  const [status, setStatus] = useState({ phase: "idle", log: [], ok: null });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/admin/upgrade/status", { headers: authHeaders() });
      setStatus(data || { phase: "idle", log: [], ok: null });
      return data;
    } catch (e) {
      // backend likely restarting — keep last known status
      return null;
    }
  };

  const clearStatus = async () => {
    try {
      await api.delete("/admin/upgrade/status", { headers: authHeaders() });
      setStatus({ phase: "idle", log: [], ok: null });
      toast.success("Stato pulito");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const data = await fetchStatus();
      if (data && (data.phase === "done_ok" || data.phase === "done_failed")) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setBusy(false);
        if (data.phase === "done_ok") toast.success("Aggiornamento completato");
        else toast.error("Aggiornamento fallito — rollback automatico eseguito");
      }
    }, 3000);
  };

  const downloadBackup = async () => {
    setBusy(true);
    try {
      const url = `${API_BASE}/admin/backup/site?include_node_modules=${withModules ? "true" : "false"}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `barone-site-${Date.now()}.zip`;
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Backup scaricato (${(blob.size / 1024 / 1024).toFixed(1)} MB)`);
    } catch (e) {
      toast.error(`Backup fallito: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const downloadDbBackup = async () => {
    try {
      const { data } = await api.get("/admin/backup", { headers: authHeaders() });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `barone-db-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Dump database scaricato");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Seleziona un file .zip");
      return;
    }
    setPendingFile(f);
    setConfirmOpen(true);
  };

  const launchUpgrade = async () => {
    if (!pendingFile) return;
    setConfirmOpen(false);
    setBusy(true);
    setStatus({ phase: "preparing", log: ["Caricamento file..."], ok: null });
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const res = await fetch(`${API_BASE}/admin/upgrade`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          typeof body.detail === "object" ? body.detail.error : body.detail || `HTTP ${res.status}`,
        );
      }
      toast.success("Codice applicato — riavvio in corso");
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      startPolling();
    } catch (e) {
      setBusy(false);
      toast.error(`Upgrade fallito: ${e.message || e}`);
    }
  };

  const phaseClass = PHASE_TONE[status.phase] || "text-afb-ink bg-white border-[color:var(--afb-cream)]";

  return (
    <div className="space-y-6" data-testid="tech-tab-root">
      {/* Backup */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Package className="w-5 h-5 mt-1 text-afb-ink" />
            <div className="flex-1">
              <h2 className="serif text-xl text-afb-ink">Backup completo del sito</h2>
              <p className="text-sm text-afb-ink/70 mt-1">
                Scarica un archivio ZIP con tutto il codice di <code>/app</code>{" "}
                (backend + frontend). Tieni una copia su un disco esterno o su GitHub.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 pl-8">
            <Switch
              id="tech-with-modules"
              checked={withModules}
              onCheckedChange={setWithModules}
              data-testid="tech-toggle-node-modules"
            />
            <Label htmlFor="tech-with-modules" className="text-sm">
              Includi <code>node_modules</code> (backup completo, file più grande)
            </Label>
          </div>

          <div className="flex flex-wrap gap-3 pl-8">
            <Button
              onClick={downloadBackup}
              disabled={busy}
              data-testid="tech-btn-download-site"
              className="bg-afb-gold hover:bg-afb-gold/90 text-afb-ink"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica backup sito (ZIP)
            </Button>
            <Button
              variant="outline"
              onClick={downloadDbBackup}
              data-testid="tech-btn-download-db"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica dump database (JSON)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* OTA Upgrade */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Cloud className="w-5 h-5 mt-1 text-afb-ink" />
            <div className="flex-1">
              <h2 className="serif text-xl text-afb-ink">
                Aggiornamento OTA (codice da ZIP)
              </h2>
              <p className="text-sm text-afb-ink/70 mt-1">
                Carica un archivio ZIP contenente <code>backend/</code> e/o{" "}
                <code>frontend/</code>. Il sistema crea uno snapshot di ripristino,
                applica i nuovi file, reinstalla le dipendenze se necessario, riavvia
                il servizio e — <strong>in caso di errore — esegue automaticamente il rollback</strong>.
              </p>
            </div>
          </div>

          <div className="pl-8 space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              onChange={onFileChosen}
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-afb-ink/20 file:text-sm file:font-medium file:bg-white file:text-afb-ink hover:file:bg-afb-cream/40"
              disabled={busy}
              data-testid="tech-input-upgrade-zip"
            />

            <div
              className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${phaseClass}`}
              data-testid="tech-status"
            >
              <div className="flex items-center gap-2">
                {busy && status.phase !== "done_ok" && status.phase !== "done_failed" ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Server className="w-4 h-4" />
                )}
                <span data-testid="tech-status-phase">
                  {PHASE_LABELS[status.phase] || status.phase}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchStatus}
                data-testid="tech-status-refresh"
                className="h-7 px-2"
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
            </div>

            {(status.phase === "done_ok" || status.phase === "done_failed") && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearStatus}
                  data-testid="tech-status-clear"
                  className="text-xs text-afb-ink/60 hover:text-afb-ink"
                >
                  Pulisci stato
                </Button>
              </div>
            )}

            {Array.isArray(status.log) && status.log.length > 0 ? (
              <pre
                className="bg-[#0b1320] text-[#d6e4ff] text-xs p-3 rounded-md max-h-56 overflow-auto"
                data-testid="tech-status-log"
              >
{status.log.join("\n")}
              </pre>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Deployment scripts */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Server className="w-5 h-5 mt-1 text-afb-ink" />
            <div className="flex-1">
              <h2 className="serif text-xl text-afb-ink">Deployment Contabo VPS</h2>
              <p className="text-sm text-afb-ink/70 mt-1">
                Script pronti per installare/aggiornare il sito su un server Linux
                (Contabo) — backend in porta <strong>8004</strong>, dominio{" "}
                <strong>baronefunebre.it</strong>. Copia, carica via SSH e lancia come root.
              </p>
              <p className="text-xs text-afb-ink/60 mt-1">
                Backend URL configurato:{" "}
                <code>{BACKEND_URL}</code>
              </p>
            </div>
          </div>

          <ScriptBox
            title="Installazione iniziale VPS"
            filename="install.sh"
            body={INSTALL_SH}
            icon={Server}
          />
          <ScriptBox
            title="Upgrade con rollback (lato VPS)"
            filename="upgrade.sh"
            body={UPGRADE_SH}
            icon={Cloud}
          />
          <ScriptBox
            title="Push backup su GitHub"
            filename="github-push.md"
            body={GITHUB_PUSH_MD}
            icon={Package}
          />
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="tech-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Conferma aggiornamento OTA</DialogTitle>
            <DialogDescription>
              Verrà creato uno snapshot della versione attuale, poi applicato lo ZIP
              <strong> {pendingFile?.name} </strong>
              ({pendingFile ? (pendingFile.size / 1024 / 1024).toFixed(2) : 0} MB).
              <br /><br />
              In caso di errore (servizio non risponde dopo il riavvio) il sistema
              ripristinerà <em>automaticamente</em> la versione precedente.
              <br /><br />
              Procedere?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setConfirmOpen(false); setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              data-testid="tech-confirm-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={launchUpgrade}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="tech-confirm-ok"
            >
              <Upload className="w-4 h-4 mr-2" />
              Avvia upgrade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
